import { AlertingIntegrationsDTO, AlertLevel, AnomalyCheckBody, CreateAlertGroupDTO, CreateAnomalyAlertGroupDTO, CreatePingAlertGroupDTO, CreatePredictiveAlertGroupDTO, FullAlertsResponseDTO } from '@dto/alerting.dto';
import { ClassesResponseDTO, HwTypesResponseDTO, SubsystemsResponseDTO } from '@dto/architecture.dto';
import { BasicResponseDTO, TextResponseDTO } from '@dto/basicResponse.dto';
import { CustomGroupsResponseDTO } from '@dto/customGroup.dto';
import { FindDashboardItemDTO, FindItemsDTO } from "@dto/dashboard.dto";
import { AlertingDetectionCapabilityDTO, SingleTimeRangeGraphResponseDTO, StateTimelineResponseDTO } from '@dto/pageResponse.dto';
import { SaveZabbixIntegration, ZabbixIntegrationsDTO } from '@dto/zabbix.dto';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { deleteApi, getApi, postApi, putApi } from 'src/util/apiCalls';
import { API_URL } from '../../../data/Api';
import { Log } from '../../../service/log.service';

export const alertingAbortCtrl: { current?: AbortController; } = {};

//TODO: make separate glob promise
export function getAllSubsystems(type?: string) {
  // TODO: return controller for abort in caller
  // const controller = new AbortController();
  const promise = axios.get<SubsystemsResponseDTO>(type ? API_URL.ARCHITECTURE + `/subsystems?type=${type}` : API_URL.ARCHITECTURE + `/subsystems`);
  promise.catch((reason: AxiosError) => Log.error('Failed to get subsystems!', reason));
  return promise;
}

export async function getCustomGroups() {
  const promise = getApi<CustomGroupsResponseDTO>("/api/custom_group/v1/custom_groups", alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get custom groups!', reason));
  return promise;
}

export async function getClasses(config?: AxiosRequestConfig) {
  const promise = axios.get<ClassesResponseDTO>(API_URL.ARCHITECTURE + '/classes?active=true', config);
  promise.catch((reason: AxiosError) => Log.error('Failed to get classes!', reason));
  return promise;
}

export async function getAclHWtypes(config?: AxiosRequestConfig) {
  const promise = getApi<HwTypesResponseDTO>(API_URL.ARCHITECTURE + '/hw_types', alertingAbortCtrl.current.signal, config);
  promise.catch((reason: AxiosError) => Log.error('Failed to get HW types!', reason));
  return promise;
}

export async function postAlertGroup(postData: CreateAlertGroupDTO) {
  const promise = postApi<FullAlertsResponseDTO, CreateAlertGroupDTO[]>(API_URL.ALERTING + "/alertGroups", [postData]);
  promise.catch((reason: AxiosError) => Log.error('Failed to create new alert group', reason));
  return promise;
}

export async function postAnomalyAlertGroup(postData: CreateAnomalyAlertGroupDTO) {
  const promise = postApi<FullAlertsResponseDTO, CreateAnomalyAlertGroupDTO[]>(API_URL.ALERTING + "/anomalyAlertGroups", [postData]);
  promise.catch((reason: AxiosError) => Log.error('Failed to create new anomaly alert group', reason));
  return promise;
}

export async function postPingAlertGroup(postData: CreatePingAlertGroupDTO) {
  const promise = postApi<FullAlertsResponseDTO, CreatePingAlertGroupDTO[]>(API_URL.ALERTING + "/pingAlertGroups", [postData]);
  promise.catch((reason: AxiosError) => Log.error('Failed to create new ping alert group', reason));
  return promise;
}

export async function postPredictionAlertGroup(postData: CreatePredictiveAlertGroupDTO) {
  const promise = postApi<FullAlertsResponseDTO, CreatePredictiveAlertGroupDTO[]>(API_URL.ALERTING + "/predictiveAlertGroups", [postData]);
  promise.catch(reason => Log.error('Failed to create new prediction alert group', reason));
  return promise;
}

export async function checkAnomaly(postData: AnomalyCheckBody) {
  const promise = postApi<BasicResponseDTO, AnomalyCheckBody>(API_URL.ALERTING + "/anomaly/check", postData);
  promise.catch((reason: AxiosError) => Log.error('Failed to check anomaly', reason));
  return promise;
}

export async function putAlertGroup(cag: CreateAlertGroupDTO) {
  const promise = putApi<FullAlertsResponseDTO, CreateAlertGroupDTO>(API_URL.ALERTING + '/alert_group/' + cag.alerting_group_id, cag);
  promise.catch((reason: AxiosError) => Log.error('Failed to update alert group', reason));
  return promise;
}

export async function putAnomalyAlertGroup(cag: CreateAnomalyAlertGroupDTO) {
  const promise = putApi<FullAlertsResponseDTO, CreateAnomalyAlertGroupDTO>(API_URL.ALERTING + '/anomaly_alert_group/' + cag.alerting_group_id, cag);
  promise.catch((reason: AxiosError) => Log.error('Failed to update anomaly alert group', reason));
  return promise;
}

export async function putPingAlertGroup(cag: CreatePingAlertGroupDTO) {
  const promise = putApi<FullAlertsResponseDTO, CreatePingAlertGroupDTO>(API_URL.ALERTING + '/ping_alert_group/' + cag.alerting_group_id, cag);
  promise.catch((reason: AxiosError) => Log.error('Failed to update ping alert group', reason));
  return promise;
}

export async function putPredictionAlertGroup(cag: CreatePredictiveAlertGroupDTO) {
  const promise = putApi<FullAlertsResponseDTO, CreatePredictiveAlertGroupDTO>(API_URL.ALERTING + '/predictive_alert_group/' + cag.alerting_group_id, cag);
  promise.catch(reason => Log.error('Failed to update predictive alert group', reason));
  return promise;
}

export async function getAlertGroupTimeline(alertingGroupId: string) {
  const promise = postApi<StateTimelineResponseDTO, { start?: number; end?: number; }>(API_URL.ALERTING + `/alertGroups/${alertingGroupId}/timeline`, {});
  promise.catch((reason: AxiosError) => Log.error('Failed to get alert group timeline!', reason));
  return promise;
}

export async function getAlertGroupItemsTimeline(alertingGroupId: string, metric: string, level?: AlertLevel) {
  const promise = postApi<StateTimelineResponseDTO, { metric: string; level?: AlertLevel; start?: number; end?: number; }>(API_URL.ALERTING + `/alertGroups/${alertingGroupId}/timeline/items`, { metric, level });
  promise.catch((reason: AxiosError) => Log.error('Failed to get alert item timeline!', reason));
  return promise;
}

export async function getAlertGroupItemGraph(alertingGroupId: string, itemId: string, metric: string, level: AlertLevel | undefined, start: number, end: number) {
  const promise = postApi<SingleTimeRangeGraphResponseDTO, { itemId: string; metric: string; level?: AlertLevel; start: number; end: number; }>(
    API_URL.ALERTING + `/alertGroups/${alertingGroupId}/timeline/items/graph`, { itemId, metric, level, start, end }
  );
  promise.catch((reason: AxiosError) => Log.error('Failed to get item graph!', reason));
  return promise;
}

export function getAlertGroups() {
  const promise = getApi<FullAlertsResponseDTO>(API_URL.ALERTING + '/alertGroups', alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get alert groups!', reason));
  return promise;
}

export function getAnomalyGroups() {
  const promise = getApi<FullAlertsResponseDTO>(API_URL.ALERTING + '/anomalyAlertGroups', alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get anomaly alert groups!', reason));
  return promise;
}

export function getPingGroups() {
  const promise = getApi<FullAlertsResponseDTO>(API_URL.ALERTING + '/pingAlertGroups', alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get ping alert groups!', reason));
  return promise;
}

export function getPredictionGroups() {
  const promise = getApi<FullAlertsResponseDTO>(API_URL.ALERTING + '/predictiveAlertGroups', alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get prediction alert groups!', reason));
  return promise;
}

export function getAlertsInDashboard() {
  const promise = postApi<FindItemsDTO, FindDashboardItemDTO>(API_URL.DASHBOARD_FIND_ITEM, { type: "alerting_table" });
  promise.catch(reason => Log.error('Failed to get alerts in dashboard!', reason));
  return promise;
}

export function getAlertIntegrations() {
  const promise = getApi<AlertingIntegrationsDTO>(API_URL.ALERTING_INTEGRATIONS, alertingAbortCtrl.current.signal);
  promise.catch(reason => Log.error('Failed to get alerting integrations!', reason));
  return promise;
}

export function getZabbixIntegrations() {
  const promise = getApi<ZabbixIntegrationsDTO>(API_URL.ZABBIX_INTEGRATIONS, alertingAbortCtrl.current.signal);
  promise.catch(reason => Log.error('Failed to get zabbix integrations!', reason));
  return promise;
}

export async function postZabbixIntegration(data: SaveZabbixIntegration) {
  const promise = postApi<BasicResponseDTO, SaveZabbixIntegration>(API_URL.ZABBIX_INTEGRATION, data);
  promise.catch((reason: AxiosError) => Log.error('Failed to create zabbix integration', reason));
  return promise;
}

export async function putZabbixIntegration(id: string, data: SaveZabbixIntegration) {
  const promise = putApi<BasicResponseDTO, SaveZabbixIntegration>(API_URL.ZABBIX_INTEGRATION + '/' + id, data);
  promise.catch((reason: AxiosError) => Log.error('Failed to update zabbix integration', reason));
  return promise;
}

export async function deleteZabbixIntegration(id: string) {
  const promise = deleteApi<BasicResponseDTO>(API_URL.ZABBIX_INTEGRATION + '/' + id);
  promise.catch((reason: AxiosError) => Log.error('Failed to delete zabbix integration', reason));
  return promise;
}

export function deleteAlertGroups(alertGroupIds: string[]) {
  const promise = axios.delete<string[], AxiosResponse<BasicResponseDTO>>(API_URL.ALERTING + '/alertGroups', { data: alertGroupIds });
  promise.catch((reason: AxiosError) => Log.error('Failed to delete alert groups!', reason));
  return promise;
}

export async function getLogs(lines: number) {
  const promise = getApi<TextResponseDTO>("/api/log/alerting?lines=" + lines, alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to get alerting logs!', reason));
  return promise;
}

export async function anomalyEnabled() {
  const promise = getApi<AlertingDetectionCapabilityDTO>("/api/alerting/v1/analyzerCheck", alertingAbortCtrl.current.signal);
  promise.catch((reason: AxiosError) => Log.error('Failed to check Analyzer state!', reason));
  return promise;
}

